/**
 * The first run tour's script and its state.
 *
 * The state lives outside React, in the same external store shape the toast
 * host uses, because three unrelated places drive it: the projects screen
 * arms it, the help menu replays it, and the overlay itself advances it.
 * Threading a context through the shell for a five step overlay would cost
 * more than it is worth.
 *
 * COMPLETION is one boolean, prefs.tourDone, and both endings write it. A
 * person who presses "Skip the tour" has made a decision, and a product that
 * asks again next week has decided their decision did not count. The flag is
 * persisted with the rest of prefs under the sv-cloud-demo localStorage key,
 * so a refresh does not replay it, a scenario switch does not replay it, and
 * the demo dock's "Reset the demo" does, which is the retrigger a demo needs.
 *
 * THE SCRIPT is five steps and every one of them points at a real element that
 * is on the screen at that moment. A step that describes something the person
 * cannot see is a slide, not a tour.
 */

import { useSyncExternalStore } from 'react';
import { captureEvent } from '../analytics/posthog';
import { useDemoStore } from '../mock/store';
import { DETAIL_TOGGLE_ENABLED } from './use-advanced';

export interface TourStep {
  /**
   * The data-tour value of the element this step points at. The overlay finds
   * it in the document at render time rather than being handed a ref, so a
   * step can point at the rail, the top bar or the page without any of them
   * knowing the tour exists beyond one attribute.
   */
  anchor: string;
  /**
   * Tried when the first anchor is not on the page. Step two points at a
   * project row, and a first time account has none, so it falls back to the
   * block on the empty screen that explains what a project is.
   */
  fallbackAnchor?: string;
  /**
   * A second element the same step points at, ringed alongside the first.
   * The card still follows the primary anchor: two rings say "these two
   * things are the same idea", and a card that tried to sit next to both
   * would sit next to neither. Used by the account step, whose destination
   * is in the rail and whose menu is in the opposite corner.
   */
  alsoAnchor?: string;
  /** English strings. They go through t() at render, never before. */
  title: string;
  body: string;
}

/**
 * Four steps, in the order a person meets them: the one thing to do, the meter
 * that says what it costs, where their own settings live, and the way between
 * projects once there is more than one. Nothing here congratulates anybody.
 *
 * It was five. The two that went were a step pointing at a project row, which
 * a first run account does not have, and a step pointing at the rail as "the
 * parts of a project", which a first run account cannot open either. Both
 * described a screen the person being toured had not reached yet, which is
 * the one thing this file's header says a step may not do.
 */
export const TOUR_STEPS: TourStep[] = [
  {
    anchor: 'new-project',
    title: 'Start here',
    body: 'Create a new project, name it, connect it to GitHub and start building.',
  },
  {
    anchor: 'nav-usage',
    title: 'What you have used',
    body: 'Usage shows how much of your plan you have spent: compute, database and files, each against its own limit.',
  },
  {
    // Two rings, one idea. The rail item is the destination and the avatar is
    // the shortcut to the same place, and a person who learns only one of
    // them goes hunting for the other.
    anchor: 'nav-account',
    alsoAnchor: 'account-menu',
    title: 'Your account',
    body: DETAIL_TOGGLE_ENABLED
      ? 'View your account settings: your profile, how the console looks, and how much detail it shows you.'
      : 'View your account settings: your profile and how the console looks.',
  },
  {
    anchor: 'project-switcher',
    title: 'Quick navigation',
    body: 'Quickly jump between projects.',
  },
];

/* ------------------------------------------------------------------ state */

export interface TourSnapshot {
  running: boolean;
  index: number;
}

let snapshot: TourSnapshot = { running: false, index: 0 };
const listeners = new Set<() => void>();

function commit(next: TourSnapshot): void {
  snapshot = next;
  if (next.running) {
    const step = TOUR_STEPS[next.index];
    captureEvent('tour_step_viewed', {
      step_index: next.index,
      step_title: step?.title,
      step_anchor: step?.anchor,
      total_steps: TOUR_STEPS.length,
    });
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function read(): TourSnapshot {
  return snapshot;
}

/** Run it from the top. Used by the auto start and by "Replay the tour". */
export function startTour(): void {
  captureEvent('tour_started', { total_steps: TOUR_STEPS.length });
  commit({ running: true, index: 0 });
}

/**
 * End it, either way. Finishing the last step and skipping on step one land
 * here together on purpose: both mean the person is done being told things.
 */
export function endTour(skipped = false): void {
  if (skipped || snapshot.index < TOUR_STEPS.length - 1) {
    captureEvent('tour_skipped', {
      step_index_at_skip: snapshot.index,
      step_title_at_skip: TOUR_STEPS[snapshot.index]?.title,
    });
  } else {
    captureEvent('tour_completed', {
      total_steps: TOUR_STEPS.length,
    });
  }
  commit({ running: false, index: 0 });
  useDemoStore.getState().setPref('tourDone', true);
}

export function nextTourStep(): void {
  if (!snapshot.running) return;
  if (snapshot.index + 1 >= TOUR_STEPS.length) {
    endTour(false);
    return;
  }
  captureEvent('tour_step_next_clicked', { current_index: snapshot.index });
  commit({ running: true, index: snapshot.index + 1 });
}

export function previousTourStep(): void {
  if (!snapshot.running || snapshot.index === 0) return;
  captureEvent('tour_step_prev_clicked', { current_index: snapshot.index });
  commit({ running: true, index: snapshot.index - 1 });
}

/** The live tour state. Safe to call from anywhere inside the shell. */
export function useTour(): TourSnapshot {
  return useSyncExternalStore(subscribe, read, read);
}

/** Whether the tour has already been finished or skipped. */
export function useTourDone(): boolean {
  return useDemoStore((s) => s.prefs.tourDone === true);
}
